<template>
    <div class="flex flex-col items-center gap-3.5 text-center py-2">
        <span class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-valence-pink-500 text-white text-[32px]" aria-hidden="true">
            <i class="bi bi-check-lg" aria-hidden="true"></i>
        </span>
        <h2 class="display-serif text-valence-stone-900">Self-assessment complete</h2>
        <p class="body-small text-valence-stone-500 max-w-[460px]">{{ lede }}</p>

        <BaseCard padding="md" class="w-full max-w-[460px] mt-3 flex items-start gap-3 text-left">
            <ReflectionIcon kind="sixty" size="md" class="mt-0.5" />
            <div class="flex flex-col gap-0.5 min-w-0">
                <span class="scaffold-body-small text-valence-stone-500">What's next</span>
                <span class="body-semibold text-valence-stone-900">Your 360 is still collecting</span>
                <span class="body-small text-valence-stone-500">{{ whatsNext }}</span>
            </div>
        </BaseCard>

        <div class="flex items-center justify-center gap-2 flex-wrap mt-3">
            <BaseButton theme="neutral" size="sm" class="base-btn-square" @click="emit('back')">Back to Upcoming</BaseButton>
            <BaseButton theme="primary" size="sm" class="base-btn-dark" @click="emit('view')">View my 360</BaseButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { CycleEnvelopeResponse, CycleResponseSummary } from "~vue/api/generated/reflections";
import type { UserDetails } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import ReflectionIcon from "~vue/pages/reflections/ReflectionIcon.vue";
import { computed, inject, onMounted, type Ref, ref } from "vue";

interface Props {
    cycleId: string;
}

const { cycleId } = defineProps<Props>();
const emit = defineEmits<{ back: []; view: [] }>();

const userDetails = inject<Ref<UserDetails>>("user_details");
const summary = ref<CycleResponseSummary | null>(null);

const lede = computed(() => {
    const firstName = userDetails?.value?.first_name;
    const opener = firstName ? `Nice work, ${firstName}.` : "Nice work.";
    return `${opener} When your raters finish, your 360 report will show how your view lines up against theirs.`;
});

const whatsNext = computed(() => {
    const tail = "We'll let you know the moment your report is ready.";
    if (!summary.value || summary.value.total === 0) {
        return `Your raters are still sharing their feedback. ${tail}`;
    }
    const noun = summary.value.total === 1 ? "rater has" : "raters have";
    return `${summary.value.responded} of ${summary.value.total} ${noun} responded. ${tail}`;
});

onMounted(async () => {
    const { data } = await useFetch(`/v1/reflections/cycles/${cycleId}`).json<CycleEnvelopeResponse>();
    summary.value = data.value?.cycle.response_summary ?? null;
});
</script>
